import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Components
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import MailPage from './components/mail/MailPage';
import ContactsPage from './components/mail/ContactsPage';
import UserProfile from './components/UserProfile';
import UserSettings from './components/UserSettings';
import Quotes from './components/Quotes';
import Projects from './components/Projects';
import PurchaseOrders from './components/PurchaseOrders';
import ProjectDetail from './components/ProjectDetail';
import PackingSlips from './components/PackingSlips';
import Expenses from './components/Expenses';
import SafetyTickets from './components/SafetyTickets';
import CompanyDocs from './components/CompanyDocs';
import Cameras from './components/Cameras';
import AdminPanel from './components/AdminPanel';
import SuperAdminConsole from './components/SuperAdminConsole';
import DataImport from './components/DataImport';
import MobileTimesheet from './components/MobileTimesheet';
import MobileTimesheetApp from './mobile/components/MobileTimesheetApp';
import ComprehensiveTimesheet from './mobile/components/ComprehensiveTimesheet';
import MobileInspections from './components/MobileInspections';
import DailyTimesheet from './components/DailyTimesheet';
import MobileInspectionForm from './components/MobileInspectionForm';
import DailyPSIForm from './components/DailyPSIForm';
import FieldCrewManager from './components/FieldCrewManager';
import ConfinedSpacePermit from './components/ConfinedSpacePermit';
import PublicCertVerify from './components/PublicCertVerify';
import TrainerPage from './components/TrainerPage';
import OnboardingSplash from './components/OnboardingSplash';
import OnboardingWizard from './components/OnboardingWizard';
import HROnboardingReview from './components/HROnboardingReview';
import HRAdminConsole from './components/HRAdminConsole';
import HRDashboard from './components/HRDashboard';
import HRDashboardCompact from './components/HRDashboardCompact';
import HREmployeeDetail from './components/HREmployeeDetail';
import HRReviewTracker from './components/HRReviewTracker';
import TimeOff from './components/TimeOff';
import MyCertificates from './components/MyCertificates';
import PhotoUpload from './components/PhotoUpload';
import TimesheetCalendar from './components/TimesheetCalendar';
import LiveProjectTracking from './components/LiveProjectTracking';
import ScheduleNew from './components/ScheduleNew';
import ToolCrib from './components/ToolCrib';
import SkillProfiles from './components/SkillProfiles';
import MaterialSheets from './components/MaterialSheets';
import DispatchRequests from './components/DispatchRequests';
import Leads from './components/Leads';
import RentalEquipment from './components/RentalEquipment';
import OwnedEquipment from './components/OwnedEquipment';
import Equipment from './components/Equipment';
import Consumables from './components/Consumables';
import ShopTracker from './components/ShopTracker';
import FieldTracker from './components/FieldTracker';
import FabScheduler from './components/FabScheduler';
import FleetMaintenance from './components/FleetMaintenance';
import MyPaperwork from './components/MyPaperwork';
import MergeAccounts from './components/MergeAccounts';
import PayRates from './components/PayRates';
import CompanyHub from './components/CompanyHub';
import TeamManagement from './components/TeamManagement';
import CRMDashboard from './components/CRMDashboard';
import CRMContacts from './components/CRMContacts';
import CRMContactForm from './components/CRMContactForm';
import CRMContactDetail from './components/CRMContactDetail';
import CRMCompanyView from './components/CRMCompanyView';
import RatesheetManager from './components/RatesheetManager';
import CatalogManagement from './components/CatalogManagement';
import SalesPipeline from './components/SalesPipeline';
import ChangePassword from './components/ChangePassword';
import AdminPasswordManagement from './components/AdminPasswordManagement';
import ChangelogAdmin from './components/ChangelogAdmin';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ChangelogSplash from './components/ChangelogSplash';
import AnnouncementGate from './components/AnnouncementGate';
import DispatchChat from './components/DispatchChat';
import NotificationSetup from './components/NotificationSetup';
import ManpowerSheet from './components/ManpowerSheet';
import PortalAdmin from './components/PortalAdmin';
import PortalApp from './portal/PortalApp';
import { registerServiceWorker, resyncPushIfGranted } from './lib/push';
import { startOfflineSync, primeOfflineCache, processOutbox } from './lib/offline';
import { OfflineBar } from './components/OfflineBar';
import { MasonWidget } from './components/MasonWidget';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debug log to check environment variable loading
console.log('Environment check:', {
  REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
  BACKEND_URL: BACKEND_URL,
  API: API
});

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [showSplash, setShowSplash] = useState(false);

  // Set default axios header + 401 interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Auto-logout on 401 — but only after re-checking the session: a single transient 401 from a background
    // poller used to wipe the token mid-task (crew clock-in then went out as "Bearer null").
    let revalidating = null;
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          const url = error.config?.url || '';
          const logout = () => {
            if (['locked_out', 'signed_out'].includes(error.response?.data?.code)) sessionStorage.setItem('login_notice', error.response.data.detail);
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
            window.location.href = '/login';
          };
          if (url.includes('/auth/me') || ['locked_out', 'signed_out'].includes(error.response?.data?.code)) {
            logout();
          } else if (!revalidating) {
            revalidating = axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
              .then(() => { console.warn('transient 401 ignored — session still valid:', url); })
              .catch((e) => { if (e.response?.status === 401) logout(); })
              .finally(() => { revalidating = null; });
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('token');
      console.log('🔍 Auth check - Token exists:', !!savedToken);
      if (savedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          console.log('🔍 Making auth/me request to:', `${API}/auth/me`);
          const response = await axios.get(`${API}/auth/me`);
          console.log('✅ Auth/me response:', response.data);
          setUser(response.data);
          setToken(savedToken);
          setShowSplash(true);
          localStorage.setItem('me_cache', JSON.stringify(response.data));
          // Sliding session: under 2 days left on the token → swap it for a fresh 7-day one
          try {
            const exp = JSON.parse(atob(savedToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp;
            if (exp && exp * 1000 - Date.now() < 2 * 24 * 60 * 60 * 1000) {
              const r = await axios.post(`${API}/auth/refresh`);
              if (r.data?.access_token) {
                localStorage.setItem('token', r.data.access_token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${r.data.access_token}`;
                setToken(r.data.access_token);
              }
            }
          } catch (e) { console.warn('token refresh skipped:', e.message); }
        } catch (error) {
          console.error('❌ Auth/me failed:', error.response?.data || error.message);
          const cached = localStorage.getItem('me_cache');
          if (!error.response && cached) {
            // No signal (not a rejected token): stay signed in on this phone with what we knew last time
            setUser(JSON.parse(cached));
            setToken(savedToken);
          } else {
            if (error.response?.data?.code === 'locked_out') sessionStorage.setItem('login_notice', error.response.data.detail);
            localStorage.removeItem('token');
            localStorage.removeItem('me_cache');
            delete axios.defaults.headers.common['Authorization'];
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Push notifications: register the service worker once, re-link this device's
  // subscription to whoever is logged in, and follow notification taps to the right page.
  useEffect(() => {
    registerServiceWorker();
    startOfflineSync();
    const onMsg = (e) => {
      if (e.data?.type === 'NAVIGATE' && e.data.url) {
        const u = new URL(e.data.url, window.location.origin);
        if (u.pathname + u.search !== window.location.pathname + window.location.search) {
          window.location.href = u.pathname + u.search;
        }
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, []);
  useEffect(() => { if (user) { resyncPushIfGranted(); setTimeout(primeOfflineCache, 3000); setTimeout(processOutbox, 2000); } }, [user]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('me_cache', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      setShowSplash(true);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    const t = localStorage.getItem('token');
    if (t) axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('me_cache');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, register, token, loading }}>
      <div className="App min-h-screen">
        {user && <OfflineBar />}
        {user && <MasonWidget />}
        <BrowserRouter>
          <Routes>
            {/* Customer Portal — its own login/session, fully separate from the internal app */}
            <Route path="/portal/*" element={<PortalApp />} />
            {/* Trainer link — no login; the token in the URL opens exactly one training session */}
            <Route path="/trainer/:token" element={<TrainerPage />} />
            <Route path="*" element={
          user ? (
            // Check if user needs onboarding (skip for HR and super_admin)
            !['hr', 'super_admin'].includes(user.role) && !user.is_onboarded ? (
              <OnboardingWizard onComplete={() => window.location.reload()} />
            ) : (
            <>
            <AnnouncementGate />
            <Routes>
              {/* Fullscreen routes without sidebar */}
              <Route path="/onboarding-video" element={<OnboardingSplash onComplete={() => window.location.reload()} />} />
              <Route path="/verify/:token" element={<PublicCertVerify />} />
              
              {/* Fullscreen mobile routes - no sidebar/header */}
              <Route path="/mobile/inspections" element={<MobileInspections />} />
              <Route path="/mobile/field-crew" element={<FieldCrewManager />} />
              <Route path="/mobile/permit/:inspectionId" element={<ConfinedSpacePermit />} />
              <Route path="/daily-timesheet" element={<DailyTimesheet />} />
              <Route path="/mobile/inspection/daily_psi" element={<DailyPSIForm />} />
              <Route path="/mobile/inspection/daily_psi/:inspectionId" element={<DailyPSIForm />} />
              <Route path="/mobile/inspection/:inspectionType" element={<MobileInspectionForm />} />
              <Route path="/mobile/inspection/:inspectionType/:inspectionId" element={<MobileInspectionForm />} />
              
              {/* Regular routes with sidebar */}
              <Route path="*" element={
                <div className="flex h-screen overflow-hidden">
                  {showSplash && <ChangelogSplash onDismiss={() => setShowSplash(false)} />}
                  <Sidebar />
                  <div className="flex-1 flex flex-col overflow-hidden">
                    <Header />
                    <main className="flex-1 overflow-y-auto p-3 lg:p-6">
                      <Routes>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/dashboard/:slug" element={<Dashboard />} />
                        <Route path="/mail" element={<MailPage />} />
                        <Route path="/mail/contacts" element={<ContactsPage />} />
                        <Route path="/profile" element={<UserProfile />} />
                        <Route path="/settings" element={<UserSettings />} />
                        <Route path="/quotes" element={<Quotes />} />
                        <Route path="/projects" element={<Projects />} />
                        <Route path="/purchase-orders" element={<PurchaseOrders />} />
                        <Route path="/projects/:projectNumber" element={<ProjectDetail />} />
                        <Route path="/packing-slips" element={<PackingSlips />} />
                        <Route path="/expenses" element={<Expenses />} />
                        <Route path="/safety" element={<CompanyDocs />} />
                        <Route path="/safety-tickets" element={<SafetyTickets />} />
                        <Route path="/admin" element={<AdminPanel />} />
                        <Route path="/admin/console" element={<SuperAdminConsole />} />
                        <Route path="/admin/password-management" element={<AdminPasswordManagement />} />
                        <Route path="/admin/changelog" element={<ChangelogAdmin />} />
                        <Route path="/cameras" element={<Cameras />} />
                        <Route path="/timesheets" element={<MobileTimesheet />} />
                        <Route path="/mobile" element={<MobileTimesheetApp />} />
                        <Route path="/mobile/timesheet-comprehensive" element={<ComprehensiveTimesheet />} />
                        <Route path="/timesheet-calendar" element={<TimesheetCalendar />} />
                        <Route path="/schedule" element={<ScheduleNew />} />
                        <Route path="/tool-crib" element={<ToolCrib />} />
                        <Route path="/skills" element={<SkillProfiles />} />
                        <Route path="/material-sheets" element={<MaterialSheets />} />
                        <Route path="/dispatch-requests" element={<DispatchRequests />} />
                        <Route path="/dispatch-chat" element={<DispatchChat />} />
                        <Route path="/manpower" element={<ManpowerSheet />} />
                        <Route path="/dailies" element={<ManpowerSheet />} />
                        <Route path="/portal-admin" element={<PortalAdmin />} />
                        <Route path="/notifications" element={<NotificationSetup />} />
                        <Route path="/leads" element={<Leads />} />
                        <Route path="/equipment" element={<Equipment />} />
                        <Route path="/rental-equipment" element={<RentalEquipment />} />
                        <Route path="/owned-equipment" element={<OwnedEquipment />} />
                        <Route path="/consumables" element={<Consumables />} />
                        <Route path="/shop-tracker" element={<ShopTracker />} />
                        <Route path="/field-tracker" element={<FieldTracker />} />
                        <Route path="/fab-scheduler" element={<FabScheduler />} />
                        <Route path="/fleet-maintenance" element={<FleetMaintenance />} />
                        <Route path="/my-paperwork" element={<MyPaperwork />} />
                        <Route path="/admin/merge-accounts" element={<MergeAccounts />} />
                        <Route path="/admin/pay-rates" element={<PayRates />} />
                        <Route path="/company" element={<CompanyHub />} />
                        <Route path="/company-hub" element={<Navigate to="/company" replace />} />
                        <Route path="/kiosk" element={<KioskActivator />} />
                        <Route path="/team" element={<TeamManagement />} />
                        <Route path="hr/onboarding" element={<HROnboardingReview />} />
                        <Route path="hr/admin" element={<HRAdminConsole />} />
                        <Route path="hr/dashboard" element={<Navigate to="/safety-tickets" replace />} />
                        <Route path="hr/reviews" element={<HRReviewTracker />} />
                        <Route path="/time-off" element={<TimeOff />} />
                        <Route path="hr/employee/:employeeEmail" element={<HREmployeeDetail />} />
                        <Route path="/my-certificates" element={<MyCertificates />} />
                        <Route path="/photo-upload" element={<PhotoUpload />} />
                        <Route path="/change-password" element={<ChangePassword />} />
                        <Route path="/crm" element={<CRMDashboard />} />
                        <Route path="/crm/pipeline" element={<SalesPipeline />} />
                        <Route path="/crm/contacts" element={<CRMContacts />} />
                        <Route path="/crm/contacts/new" element={<CRMContactForm />} />
                        <Route path="/crm/contacts/:id" element={<CRMContactDetail />} />
                        <Route path="/crm/ratesheets" element={<RatesheetManager />} />
                        <Route path="/crm/contacts/:id/edit" element={<CRMContactForm />} />
                        <Route path="/crm/company/:companyName" element={<CRMCompanyView />} />
                        <Route path="/catalog" element={<CatalogManagement />} />
                        <Route path="/import" element={<DataImport />} />
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      </Routes>
                    </main>
                  </div>
                </div>
              } />
            </Routes>
            </>
            )
          ) : localStorage.getItem('shop_kiosk_mode') === 'true' ? (
            <KioskLoginScreen login={login} />
          ) : (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/verify/:token" element={<PublicCertVerify />} />
              <Route path="/mobile" element={<MobileTimesheetApp />} />
              <Route path="/kiosk" element={<KioskActivator />} />
              <Route path="/" element={<Navigate to="/login" replace />} />
              {/* Any other path while logged out (e.g. /shop-tracker after exiting
                  kiosk via Ctrl+3, or a forklift QR deep link) must fall back to login,
                  preserving the intended destination so we can return after sign-in. */}
              <Route path="*" element={<RedirectToLogin />} />
            </Routes>
          )} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthContext.Provider>
  );
}

function RedirectToLogin() {
  const loc = useLocation();
  const target = loc.pathname + loc.search;
  const qs = target && target !== '/' ? `?redirect=${encodeURIComponent(target)}` : '';
  return <Navigate to={`/login${qs}`} replace />;
}

function KioskActivator() {
  React.useEffect(() => {
    localStorage.setItem('shop_kiosk_mode', 'true');
    window.location.replace('/');
  }, []);
  return null;
}

function KioskLoginScreen({ login }) {
  const [showLogin, setShowLogin] = React.useState(false);
  const [step, setStep] = React.useState('number');
  const [empNumber, setEmpNumber] = React.useState('');
  const [pin, setPin] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showKioskPin, setShowKioskPin] = React.useState(false);
  const [kioskPin, setKioskPin] = React.useState('');
  const [firstNewPin, setFirstNewPin] = React.useState('');
  const [pinToken, setPinToken] = React.useState('');
  const tapCount = React.useRef(0);
  const tapTimer = React.useRef(null);
  
  const idleTimer = React.useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;


  const handleKioskPinSubmit = () => {
    if (kioskPin === '3650') {
      if (localStorage.getItem('shop_kiosk_mode') === 'true') {
        localStorage.removeItem('shop_kiosk_mode');
      } else {
        localStorage.setItem('shop_kiosk_mode', 'true');
      }
      window.location.reload();
    } else {
      setKioskPin('');
      setError('Invalid PIN');
      setTimeout(() => setError(''), 2000);
    }
    setShowKioskPin(false);
    setKioskPin('');
  };

  // Ctrl+3 to toggle kiosk mode (keyboard shortcut still works)
  React.useEffect(() => {
    const handleKioskShortcut = (e) => {
      if (e.ctrlKey && e.key === '3') {
        e.preventDefault();
        if (localStorage.getItem('shop_kiosk_mode') === 'true') {
          localStorage.removeItem('shop_kiosk_mode');
        } else {
          localStorage.setItem('shop_kiosk_mode', 'true');
        }
        window.location.reload();
      }
    };
    window.addEventListener('keydown', handleKioskShortcut);
    return () => window.removeEventListener('keydown', handleKioskShortcut);
  }, []);


  React.useEffect(() => {
    if (showLogin) {
      const resetIdle = () => {
        clearTimeout(idleTimer.current);
        idleTimer.current = setTimeout(() => { setShowLogin(false); setEmpNumber(''); setPin(''); setError(''); setStep('number'); }, 60000);
      };
      resetIdle();
      const handler = () => resetIdle();
      const keyHandler = (e) => {
        resetIdle();
        if (e.key >= '0' && e.key <= '9') pressKey(e.key);
        else if (e.key === 'Backspace') pressKey('back');
        else if (e.key === 'Escape') pressKey('clear');
      };
      window.addEventListener('touchstart', handler);
      window.addEventListener('click', handler);
      window.addEventListener('keydown', keyHandler);
      return () => { clearTimeout(idleTimer.current); window.removeEventListener('touchstart', handler); window.removeEventListener('click', handler); window.removeEventListener('keydown', keyHandler); };
    }
  }, [showLogin, step, empNumber, pin]);

  const pressKey = (key) => {
    setError('');
    if (step === 'number') {
      if (key === 'back') { setEmpNumber(e => e.slice(0, -1)); return; }
      if (key === 'clear') { setEmpNumber(''); return; }
      if (empNumber.length < 4) {
        const next = empNumber + key;
        setEmpNumber(next);
        if (next.length === 4) {
          if (next === '9999') {
            localStorage.removeItem('shop_kiosk_mode');
            window.location.reload();
            return;
          }
          setTimeout(() => setStep('pin'), 300);
        }
      }
    } else if (step === 'pin') {
      if (key === 'back') { setPin(p => p.slice(0, -1)); return; }
      if (key === 'clear') { setPin(''); setStep('number'); setEmpNumber(''); return; }
      if (pin.length < 4) {
        const next = pin + key;
        setPin(next);
        if (next.length === 4) setTimeout(() => doLogin(empNumber, next), 300);
      }
    } else if (step === 'newpin') {
      if (key === 'back') { setPin(p => p.slice(0, -1)); return; }
      if (key === 'clear') { setPin(''); return; }
      if (pin.length < 4) {
        const next = pin + key;
        setPin(next);
        if (next.length === 4) {
          if (next === '1234') {
            setError('Please choose a PIN other than 1234');
            setTimeout(() => { setPin(''); setError(''); }, 1600);
            return;
          }
          setFirstNewPin(next);
          setTimeout(() => { setPin(''); setStep('confirmnew'); }, 300);
        }
      }
    } else if (step === 'confirmnew') {
      if (key === 'back') { setPin(p => p.slice(0, -1)); return; }
      if (key === 'clear') { setPin(''); setStep('newpin'); setFirstNewPin(''); return; }
      if (pin.length < 4) {
        const next = pin + key;
        setPin(next);
        if (next.length === 4) {
          if (next !== firstNewPin) {
            setError('PINs do not match — try again');
            setTimeout(() => { setPin(''); setFirstNewPin(''); setStep('newpin'); setError(''); }, 1600);
            return;
          }
          setTimeout(() => savePinAndEnter(next), 300);
        }
      }
    }
  };

  const savePinAndEnter = async (newPinVal) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/set-my-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pinToken}` },
        body: JSON.stringify({ pin: newPinVal }),
      });
      if (res.ok) {
        localStorage.setItem('token', pinToken);
        window.location.reload();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to set PIN');
        setPin(''); setFirstNewPin(''); setStep('newpin');
      }
    } catch {
      setError('Connection error');
      setPin(''); setFirstNewPin(''); setStep('newpin');
    }
    setLoading(false);
  };

  const doLogin = async (num, p) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/shop/kiosk-login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_number: num, pin: p }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.must_change_pin) {
          // First login with default PIN — force a new PIN before entering
          setPinToken(data.access_token);
          setPin(''); setFirstNewPin(''); setError('');
          setStep('newpin');
        } else {
          localStorage.setItem('token', data.access_token);
          window.location.reload();
        }
      } else { setError(data.detail || 'Login failed'); setPin(''); }
    } catch { setError('Connection error'); setPin(''); }
    setLoading(false);
  };

  // Tap hidden corner zone 7 times to show kiosk admin PIN
  const handleCornerTap = (e) => {
    e.preventDefault();
    e.stopPropagation();
    tapCount.current += 1;
    clearTimeout(tapTimer.current);
    if (tapCount.current >= 7) {
      tapCount.current = 0;
      setShowKioskPin(true);
      return;
    }
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 4000);
  };

  if (!showLogin) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#0a1628] overflow-hidden cursor-pointer select-none" onClick={() => setShowLogin(true)} data-testid="kiosk-screensaver">
        <style>{`
          @keyframes float{0%,100%{transform:translate(0,0) rotate(0deg)}25%{transform:translate(20vw,-15vh) rotate(90deg)}50%{transform:translate(-10vw,20vh) rotate(180deg)}75%{transform:translate(15vw,10vh) rotate(270deg)}}
          @keyframes drift{0%,100%{transform:translate(0,0)}33%{transform:translate(30vw,-20vh)}66%{transform:translate(-20vw,15vh)}}
          @keyframes logoBounce{0%,100%{transform:translate(-50%,-50%) scale(1)}50%{transform:translate(-50%,-50%) scale(1.05)}}
          @keyframes logoMove{0%{top:50%;left:50%}15%{top:30%;left:70%}30%{top:60%;left:25%}45%{top:35%;left:55%}60%{top:65%;left:75%}75%{top:40%;left:30%}90%{top:55%;left:60%}100%{top:50%;left:50%}}
          @keyframes pulse{0%,100%{opacity:.6}50%{opacity:1}}
          @keyframes tapHint{0%,70%,100%{opacity:0}80%,90%{opacity:.5}}
          .particle{position:absolute;border-radius:50%;pointer-events:none}
        `}</style>
        <div className="particle" style={{width:'300px',height:'300px',top:'10%',left:'20%',animation:'float 25s ease-in-out infinite',background:'radial-gradient(circle,rgba(59,130,246,.3),transparent)'}} />
        <div className="particle" style={{width:'200px',height:'200px',top:'60%',left:'70%',animation:'float 30s ease-in-out infinite 5s',background:'radial-gradient(circle,rgba(234,179,8,.15),transparent)'}} />
        <div className="particle" style={{width:'400px',height:'400px',top:'40%',left:'40%',animation:'drift 35s ease-in-out infinite 2s',background:'radial-gradient(circle,rgba(59,130,246,.1),transparent)'}} />
        <div className="particle" style={{width:'150px',height:'150px',top:'80%',left:'15%',animation:'float 20s ease-in-out infinite 8s',background:'radial-gradient(circle,rgba(16,185,129,.2),transparent)'}} />
        <div onClick={() => setShowLogin(true)} 
          style={{position:'absolute',animation:'logoMove 120s ease-in-out infinite, logoBounce 4s ease-in-out infinite',top:'50%',left:'50%',transform:'translate(-50%,-50%)',zIndex:10,cursor:'pointer'}}>
          <img src="/365ops-logo.png" alt="365Ops"
            style={{width:'768px',height:'640px',filter:'drop-shadow(0 0 50px rgba(59,130,246,.5))'}} />
          <p style={{textAlign:'center',color:'#94a3b8',fontSize:'13px',marginTop:'16px',fontWeight:'500',letterSpacing:'2px',textTransform:'uppercase',animation:'pulse 3s ease-in-out infinite'}}>Shop Time Tracker</p>
        </div>
        <div style={{position:'absolute',bottom:'40px',left:'50%',transform:'translateX(-50%)',animation:'tapHint 8s ease-in-out infinite',zIndex:10}}>
          <p style={{color:'#475569',fontSize:'16px',fontWeight:'500'}}>Tap anywhere to sign in</p>
        </div>
        {/* Hidden corner tap zone for kiosk admin — bottom-left */}
        <div onClick={handleCornerTap} style={{position:'absolute',bottom:0,left:0,width:'60px',height:'60px',zIndex:50}} />
      </div>
    );
  }

  const display = step === 'number' ? empNumber : pin;
  const maxLen = step === 'number' ? 4 : 4;
  const dots = Array.from({length: maxLen}, (_, i) => i < display.length);

  return (
    <div className="fixed inset-0 z-[100] bg-[#0a1628] flex flex-col items-center justify-center">
      {/* Kiosk Admin PIN Modal */}
      {showKioskPin && (
        <div className="absolute inset-0 z-[200] bg-black/80 flex items-center justify-center" onClick={() => { setShowKioskPin(false); setKioskPin(''); }}>
          <div className="bg-slate-900 rounded-2xl border border-slate-700 p-8 w-72" onClick={e => e.stopPropagation()}>
            <h3 className="text-white text-center font-bold mb-1">Kiosk Admin</h3>
            <p className="text-slate-500 text-center text-xs mb-4">
              {localStorage.getItem('shop_kiosk_mode') === 'true' ? 'Enter PIN to EXIT kiosk mode' : 'Enter PIN to ENABLE kiosk mode'}
            </p>
            <div className="flex justify-center gap-2 mb-4">
              {[0,1,2,3].map(i => (
                <div key={i} className={`w-4 h-4 rounded-full border-2 ${i < kioskPin.length ? 'bg-blue-500 border-blue-500' : 'border-slate-600'}`} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[1,2,3,4,5,6,7,8,9,'',0,'OK'].map((n, i) => (
                n === '' ? <div key={i} /> :
                <button key={i} onClick={() => {
                  if (n === 'OK') { handleKioskPinSubmit(); }
                  else if (kioskPin.length < 4) { setKioskPin(prev => prev + String(n)); }
                }} className={`py-3 rounded-xl text-lg font-bold transition-colors ${
                  n === 'OK' ? 'bg-emerald-600 hover:bg-emerald-700 text-white text-sm' : 'bg-slate-800 hover:bg-slate-700 text-white'
                }`}>{n}</button>
              ))}
            </div>
            <button onClick={() => { setShowKioskPin(false); setKioskPin(''); }}
              className="w-full mt-3 text-xs text-slate-500 hover:text-slate-300">Cancel</button>
          </div>
        </div>
      )}
      <div className="w-full max-w-xs px-4">
        <div className="flex justify-center mb-4">
          <img src="/365ops-logo.png" alt="365Ops" className="w-16 h-14" />
        </div>
        <h2 className="text-white text-center text-lg font-semibold mb-1">
          {step === 'number' ? 'Employee Number'
            : step === 'newpin' ? 'Set a New PIN'
            : step === 'confirmnew' ? 'Confirm New PIN'
            : 'Enter PIN'}
        </h2>
        {step === 'pin' && <p className="text-blue-400 text-center text-sm mb-3">Employee #{empNumber}</p>}
        {step === 'newpin' && <p className="text-amber-400 text-center text-sm mb-3">Your PIN is still the default — please choose a new one</p>}
        {step === 'confirmnew' && <p className="text-blue-400 text-center text-sm mb-3">Re-enter your new PIN to confirm</p>}
        <div className="flex justify-center gap-3 mb-6">
          {dots.map((filled, i) => (
            <div key={i} className={`w-5 h-5 rounded-full border-2 transition-all ${filled ? 'bg-blue-500 border-blue-500 scale-110' : 'border-slate-600'}`} />
          ))}
        </div>
        {error && <p className="text-red-400 text-sm text-center mb-3">{error}</p>}
        {loading && <p className="text-blue-400 text-sm text-center mb-3">Signing in...</p>}
        <div className="grid grid-cols-3 gap-2">
          {['1','2','3','4','5','6','7','8','9','clear','0','back'].map(key => (
            <button key={key} onClick={() => pressKey(key)} disabled={loading}
              className={`py-4 rounded-xl text-xl font-bold transition-all active:scale-95 ${
                key === 'clear' ? 'bg-slate-800 text-slate-400 text-sm hover:bg-slate-700'
                : key === 'back' ? 'bg-slate-800 text-slate-400 text-sm hover:bg-slate-700'
                : 'bg-slate-800/80 text-white hover:bg-slate-700 active:bg-blue-600'
              }`}>
              {key === 'back' ? '\u232B' : key === 'clear' ? 'CLR' : key}
            </button>
          ))}
        </div>
        <button onClick={() => { setShowLogin(false); setEmpNumber(''); setPin(''); setError(''); setStep('number'); setFirstNewPin(''); setPinToken(''); }}
          className="w-full mt-4 py-2 text-slate-500 hover:text-slate-300 text-sm">Cancel</button>
      </div>
      {/* Hidden corner tap zone for kiosk admin — bottom-left */}
      <div onClick={handleCornerTap} style={{position:'absolute',bottom:0,left:0,width:'60px',height:'60px',zIndex:50}} />
    </div>
  );
}

export default App;
